import {
  GenericMenuItemInfo
} from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { contentCountsRequest } from '~src/spacesPlatform/actions/ContentCountsActions';
import { ExplorerDropdownItemFormatter } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerDropdownPanelItems';
import {
  ExplorerListMenuPanel,
  ExplorerListMenuPanelDispatchProps,
  ExplorerListMenuPanelOwnProps,
  ExplorerListMenuPanelStateProps
} from '~src/spacesPlatform/components/Explorer/widgets/ExplorerListMenuPanel';
import { selectContentCounts, selectContentCountsUpToDate } from '~src/spacesPlatform/selectors/ContentCountsSelector';
import { selectSiteSpaceContentType } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';

import { AppState } from '../../../app/state/AppState';
import { SITE_SPACE_EXPLORE, SiteSpaceContentType } from '../../../app/state/SiteSpaceContentTypeState';
import { MapDispatchToProps, MapStateToProps } from '../../../app/utils/ConnectUtils';
import { Messages } from '../../../codegen/Localize';
import { getMixedContentInOrder } from '../../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../../ts/FeatureFlags';

function isChecked(contentType: SiteSpaceContentType, currentContentType: SiteSpaceContentType): boolean {
  return currentContentType === contentType ||
    (contentType === 'datasource' && currentContentType === 'dataconnection');
}

function getMenuItems(
  currentContentType: SiteSpaceContentType,
  contentCounts: ISpaceInfoTypeItemCount<'site'>,
  contentCountsUpToDate: boolean,
  flowsEnabled: boolean,
): GenericMenuItemInfo[] {

  let menuItems: GenericMenuItemInfo[] = [];
  menuItems.push({
    value: SITE_SPACE_EXPLORE,
    disabled: false,
    checked: isChecked(SITE_SPACE_EXPLORE, currentContentType),
    displayFormatterBoundWithData: ExplorerDropdownItemFormatter(
      Messages.siteFilterByTypeDropdown_TopLevelProjects(),
      contentCountsUpToDate && contentCounts && contentCounts['explore'],
      contentCountsUpToDate),
    keyboardAccessibleValue: Messages.siteFilterByTypeDropdown_TopLevelProjects()
  });

  menuItems = menuItems.concat(_.compact(getMixedContentInOrder().map((contentDefinition) => {
    if (
      (contentDefinition.itemType !== 'flow' || flowsEnabled) &&
      (contentDefinition.itemType !== 'metric' || FeatureFlags.metricsSpaceEnabled) &&
      (contentDefinition.itemType !== 'virtualConnection' || FeatureFlags.publishedConnectionContentManagementEnabled) &&
      (contentDefinition.itemType !== 'predictiveModel' || FeatureFlags.prometheusPredictiveModelEnabled)) {
       return {
        value: contentDefinition.itemType,
        disabled: false,
        checked: isChecked(contentDefinition.itemType, currentContentType),
        displayFormatterBoundWithData: ExplorerDropdownItemFormatter(
          contentDefinition.siteFilterByTypeDropdownName(),
          contentCountsUpToDate && contentCounts && contentCounts[contentDefinition.itemType],
          contentCountsUpToDate),
        keyboardAccessibleValue: contentDefinition.siteFilterByTypeDropdownName()
      } as GenericMenuItemInfo;
    }
  })));
  return menuItems;
}

const getMenuItemsWrapper = defaultMemoize(getMenuItems);

const mapStateToProps: MapStateToProps<ExplorerListMenuPanelStateProps> =
  (state: AppState) => {
    const currentContentType = selectSiteSpaceContentType(state);
    const contentCounts = selectContentCounts<'site'>(state, 'site');
    const contentCountsUpToDate = selectContentCountsUpToDate(state);
    const flowsEnabled = selectFlowsOnSiteEnabled(state);
    const menuItems = getMenuItemsWrapper(currentContentType, contentCounts, contentCountsUpToDate, flowsEnabled);
    return {
      menuItems
    };
  };

const mapDispatchToProjectProps: MapDispatchToProps<ExplorerListMenuPanelDispatchProps> =
  (dispatch) => bindActionCreators({
    onComponentDidUpdate: contentCountsRequest
  },
  dispatch);

export const ConnectedSiteLevelFilterMenu: React.ComponentClass<ExplorerListMenuPanelOwnProps> =
  connect(mapStateToProps, mapDispatchToProjectProps)(ExplorerListMenuPanel);
